import { CommonTooltips } from "@mcleod/common";
import { CrudDecorator, DataSourceMode, Layout, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { Alignment, Model } from "@mcleod/core";
import { AutogenLayoutMobileCommSendLoadOptionsTable } from "./autogen/AutogenLayoutMobileCommSendLoadOptionsTable";
import { MobileCommSendLoadOptions } from "./MobileCommSendLoadOptions";

export class MobileCommSendLoadOptionsTable extends AutogenLayoutMobileCommSendLoadOptionsTable {

    // Centralized constants to avoid string duplication and reduce typos.
    private static readonly OWNER_MC_CONTROL_ID = "layoutMobileCommControl";
    private static readonly COLUMN_VENDOR_HEADER_ID = "columnHeaderVendor";
    private static readonly LAYOUT_PATH_SEND_LOAD_OPTIONS = "lme/dispatch/mobilecomm/MobileCommSendLoadOptions";
    private static readonly FIELD_MC_CONTROL_ID = "mc_control_id";
    private static readonly FIELD_ID = "id";
    private static readonly FIELD_LOCATION_ID = "location_id";
    private static readonly FIELD_LOCATION_NAME = "location_name";
    private static readonly COMPONENT_LABEL_LOCATION = "labelLocation";
    private static readonly LAYOUT_LOCATION_QUICK_INFO = "lme/dispatch/LocationQuickInfo";
    private static readonly SEARCH_LOCATION_ENDPOINT = "lme/dispatch/location";
    private static readonly SEARCH_LOCATION_LAYOUT = "lme/dispatch/Location";
    private static readonly DEFAULT_LOCATION_TOOLTIP = "Default Location";

    async onLoad(): Promise<void> {
        this.configureInitialColumnVisibility();
        this.configureAddSendLoadOptionButton();
    }

    /**
     * Hides vendor column and refreshes table data when in the Mobile Comm Control context.
     */
    private configureInitialColumnVisibility(): void {
        if (this.owner.id !== MobileCommSendLoadOptionsTable.OWNER_MC_CONTROL_ID) return;

        const vendorColumn = this.tableSendLoadOptions.columns.find(
            column => column.headingCell.id === MobileCommSendLoadOptionsTable.COLUMN_VENDOR_HEADER_ID
        );
        if (vendorColumn) {
            this.tableSendLoadOptions.setColumnVisibility(vendorColumn, false);
            // Force re-display so the column hiding takes immediate effect for already bound data.
            this.tableSendLoadOptions.displayComponentData(null, this.tableSendLoadOptions.data, 0);
        }
    }

    /**
     * Wires the Add button to open a CrudDecorator for adding a send load option.
     */
    private configureAddSendLoadOptionButton(): void {
        this.buttonAddSendLoadOption.addClickListener(async () => {
            try {
                await this.handleAddSendLoadOptionClick();
            } catch (e) {
                // Intentionally swallow to avoid bubbling UI errors; real implementation could log.
                // console.error("Failed to open Send Load Option editor", e);
            }
        });
    }

    private async handleAddSendLoadOptionClick(): Promise<void> {
        const disableMcControlId = this.owner.id === MobileCommSendLoadOptionsTable.OWNER_MC_CONTROL_ID;
        const controlId = await this.owner.mainDataSource?.activeRow?.getString(MobileCommSendLoadOptionsTable.FIELD_ID);

        const layout = Layout.getLayout(MobileCommSendLoadOptionsTable.LAYOUT_PATH_SEND_LOAD_OPTIONS) as MobileCommSendLoadOptions;
        const crud = new CrudDecorator({
            layout,
            width: "75%",
            headerProps: {
                showClose: true,
                showSaveAndClose: true,
                showDelete: false,
                onExecute: () => this.addRowFromChildLayout(layout)
            },
            doAfterSlideIn: async () => {
            },
            // After the panel fully slides out (save & close), reapply the existing sort so the newly added row
            // is positioned correctly. We attempt to find the first sorted column's field name via its sort selector.
            // If no explicit sort is active we do nothing.
            doAfterSlideOut: async () => {
                try {
                    // isSorted() public API indicates whether there is any active sort.
                    if (typeof (this.tableSendLoadOptions as any).isSorted === "function" && this.tableSendLoadOptions.isSorted()) {
                        // Attempt to locate first column with an active sort selector (heuristic: selector has direction).
                        const sortedColumn: any = this.tableSendLoadOptions.columns.find((c: any) => c?.sortSelector?.direction != null);
                        const fieldName = sortedColumn?.sortSelector?.fieldName ?? "location_name";
                        const direction = sortedColumn?.sortSelector?.direction ?? "asc";
                        if (fieldName) {
                            await this.tableSendLoadOptions.applySort(fieldName, direction);
                        }
                    }
                } catch { /* swallow - non-critical enhancement */ }
            },
            layoutLoadListeners: async () => this.initializeChildLayout(layout, disableMcControlId, controlId)
        });
        crud.slideIn({ speed: 200, direction: Alignment.BOTTOM });
    }

    private addRowFromChildLayout(layout: MobileCommSendLoadOptions): void {
        if (!layout.mainDataSource?.activeRow) return;
        this.tableSendLoadOptions.addRow(layout.mainDataSource.activeRow, null, { display: true, addToData: true });
    }

    private async initializeChildLayout(
        layout: MobileCommSendLoadOptions,
        disableMcControlId: boolean,
        controlId?: string | null
    ): Promise<void> {
        if (disableMcControlId) {
            // Prepare a blank row and seed mc_control_id when coming from the MC Control context.
            await layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
            if (controlId) {
                await layout.mainDataSource.activeRow.set(MobileCommSendLoadOptionsTable.FIELD_MC_CONTROL_ID, controlId);
                await layout.mainDataSource.activeRow.setLookupModelData(
                    MobileCommSendLoadOptionsTable.FIELD_MC_CONTROL_ID,
                    { id: controlId }
                );
                await layout.textboxMcControlId.displayData(layout.mainDataSource.activeRow, null, 0);
                layout.textboxMcControlId.text = controlId;
                layout.applyVendorConfiguration(controlId);
            }
            layout.textboxMcControlId.enabled = false;
        }
        layout.mainDataSource.mode = DataSourceMode.ADD;
    }

    async tableSendLoadOptionsOnRowDisplay(event: TableRowDisplayEvent): Promise<void> {
        const tableRow = event.getTableRow();
        const locationId = tableRow.data.get(MobileCommSendLoadOptionsTable.FIELD_LOCATION_ID);
        const labelLocation = tableRow.findComponentById(MobileCommSendLoadOptionsTable.COMPONENT_LABEL_LOCATION) as Textbox;

        if (locationId === undefined || locationId === null) {
            labelLocation.tooltip = MobileCommSendLoadOptionsTable.DEFAULT_LOCATION_TOOLTIP;
            return;
        }

        CommonTooltips.setTooltipContentLayout(
            labelLocation,
            locationId,
            MobileCommSendLoadOptionsTable.LAYOUT_LOCATION_QUICK_INFO
        );

        if (tableRow.data.get(MobileCommSendLoadOptionsTable.FIELD_LOCATION_NAME) !== undefined) return;

        // Lazy load location name only if missing.
        const response = await Model.searchSingleRecord(
            MobileCommSendLoadOptionsTable.SEARCH_LOCATION_ENDPOINT,
            {
                id: locationId,
                updating_stop: false,
                _field_list: { layoutName: MobileCommSendLoadOptionsTable.SEARCH_LOCATION_LAYOUT }
            }
        );
        const name = response?.data?.name;
        if (name) {
            tableRow.data.set(MobileCommSendLoadOptionsTable.FIELD_LOCATION_NAME, name);
            labelLocation.text = name;
        }
    }
}
